import { useContext, useSyncExternalStore } from 'react';
import { openProfile } from './brand';
import { RuntimeContext, type AppRuntime } from './runtimeContext';
import type { InstallSnapshot } from './install';
import type { SessionSnapshot } from '../session/manager';
import type { EngineState } from '../sync/engine';

export function useRuntime(): AppRuntime {
  const runtime = useContext(RuntimeContext);
  if (runtime === null) throw new Error('RuntimeProvider is missing');
  return runtime;
}

export function useSession(): SessionSnapshot {
  const { session } = useRuntime();
  return useSyncExternalStore(
    (listener) => session.subscribe(listener),
    () => session.getSnapshot(),
  );
}

/** What this device can do about installing the application (D-208). */
export function useInstall(): InstallSnapshot {
  const { install } = useRuntime();
  return useSyncExternalStore(
    (listener) => install.subscribe(listener),
    () => install.getSnapshot(),
  );
}

export function useEngineState(): EngineState {
  const { engine } = useRuntime();
  return useSyncExternalStore(
    (listener) => engine.subscribe(listener),
    () => engine.getState(),
  );
}

export interface Vocabulary {
  site: string;
  sites: string;
  client: string;
  clients: string;
}

/** The organization's nouns, from the profile once signed in, from bootstrap before.
 *
 *  Its own things and only its own: its sites, its installations, its clients.
 *  The visit is the product's entity and is always called a visit, whatever the
 *  organization calls the work it does there (D-179): `visit_label_*` travels in
 *  the profile and in bootstrap and the field experience reads neither.
 *
 *  INC-16.1 (D-424): the profile's while its session is open, as the brand's
 *  (brand.ts); a session that ended reads bootstrap's, as one never begun. */
export function useVocabulary(): Vocabulary {
  const snapshot = useSession();
  const organization = openProfile(snapshot)?.organization;
  if (organization !== undefined) {
    return {
      site: organization.site_label_singular,
      sites: organization.site_label_plural,
      client: organization.client_label_singular,
      clients: organization.client_label_plural,
    };
  }
  const vocabulary = snapshot.bootstrap?.organization.vocabulary;
  return {
    site: vocabulary?.site_label_singular ?? '',
    sites: vocabulary?.site_label_plural ?? '',
    client: vocabulary?.client_label_singular ?? '',
    clients: vocabulary?.client_label_plural ?? '',
  };
}

/** The organization's geolocation policy as the profile carries it, or nothing. */
export function useGeoPolicy(): Record<string, unknown> {
  const snapshot = useSession();
  return snapshot.profile?.organization.geo_policy ?? {};
}

/** The organization's locale and timezone, for dates and times. */
export function useLocality(): { locale: string; timeZone: string } {
  const snapshot = useSession();
  const organization = snapshot.profile?.organization;
  return { locale: organization?.locale ?? 'es-AR', timeZone: organization?.timezone ?? 'UTC' };
}
